import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { roleLabel, roleBadgeStyle, rolesForUser } from "@/lib/roles";
import api from "@/lib/api";
import { AI_ENABLED } from "@/lib/features";
import useNotifications from "@/hooks/useNotifications";
import MessagingWidget from "@/components/messaging/MessagingWidget";
import BrandMark from "@/components/BrandMark";
import SupportContact from "@/components/SupportContact";
import {
  navigationKeysForUser,
  referenceKeysForUser,
  PERSONA_NAV_ITEMS,
} from "@/lib/personaNavigation";
import {
  SquaresFour,
  TestTube,
  Snowflake,
  Flask,
  Package,
  Sparkle,
  SignOut,
  UsersThree,
  Buildings,
  ClipboardText,
  ListChecks,
  ShieldCheck,
  Bank,
  Warning,
  House,
  Gear,
  Tray,
  SealCheck,
  ListDashes,
  ListMagnifyingGlass,
  CalendarBlank,
  ChartLineUp,
} from "@phosphor-icons/react";

// One glyph per destination. Flask previously stood for My Work, Supervisor
// Review and Tests at once, ShieldCheck for both QA Release and Audit Trail,
// and TestTube for both sample entries, so a supervisor scanning the sidebar
// met the same icon three times and it identified nothing.
const NAV_ICONS = {
  dashboard: SquaresFour,
  clients: Buildings,
  orders: ClipboardText,
  "register-sample": TestTube,          // the specimen itself, at intake
  samples: ListDashes,                  // the list you read status from
  storage: Snowflake,
  catalogue: ListChecks,
  worklist: Tray,                       // what has landed in your queue
  "supervisor-review": SealCheck,       // first-level approval
  "qa-release": ShieldCheck,            // final release authority
  tests: Flask,                         // the assays themselves
  inventory: Package,
  audit: ListMagnifyingGlass,           // searching the record after the fact
  ai: Sparkle,
  team: UsersThree,
  organizations: Bank,
  portal: House,
  "portal-orders": ClipboardText,
  schedule: CalendarBlank,
  "my-stats": ChartLineUp,
  configuration: Gear,
};

export default function Layout() {
  const { user, logout, organization, isPlatformAdmin } = useAuth();
  const navigate = useNavigate();
  const decorate = (key) => ({ key, ...PERSONA_NAV_ITEMS[key], icon: NAV_ICONS[key] });
  const navOptions = { aiEnabled: AI_ENABLED, isPlatformAdmin };
  const navigation = navigationKeysForUser(user, navOptions).map(decorate);
  // S-10: screens this persona reads but does not administer.
  const reference = referenceKeysForUser(user, navOptions).map(decorate);
  const location = useLocation();
  const { unread } = useNotifications({
    user,
    organization,
    pathname: location.pathname,
  });

  const handleLogout = async () => {
    // Revoke the session server-side; clearing local storage alone would leave
    // a captured token usable for the rest of its lifetime (NFR-03).
    try {
      await api.post("/auth/logout");
    } catch {
      // Already expired or revoked — signing out locally is still correct.
    }
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-white border-r border-slate-200 flex flex-col fixed h-screen">
        <div className="h-16 flex items-center gap-2.5 px-6 border-b border-slate-200">
          <div className="w-8 h-8 rounded-md bg-slate-900 flex items-center justify-center">
            <BrandMark size={20} className="text-white" />
          </div>
          <div className="leading-tight min-w-0">
            <div className="font-display font-semibold text-slate-900 text-lg">BioLIMS</div>
            <div
              data-testid="active-organization"
              title={organization ? `Organization ${organization.code}` : "No organization"}
              className="text-[10px] uppercase tracking-[0.2em] text-slate-400 truncate"
            >
              {organization ? organization.code : "No organization"}
            </div>
          </div>
        </div>

        <nav aria-label="Role-appropriate primary navigation" className="flex-1 px-3 py-6 overflow-y-auto">
          {[
            { heading: "Your workspace", items: navigation },
            { heading: "Reference", items: reference },
          ]
            .filter((group) => group.items.length > 0)
            .map(({ heading, items }, index) => (
              <div key={heading} className={index > 0 ? "mt-5" : undefined}>
                <div
                  id={`nav-group-${heading.toLowerCase().replace(/\s+/g, "-")}`}
                  className="px-3 pb-2 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400"
                >
                  {heading}
                </div>
                <ul
                  aria-labelledby={`nav-group-${heading.toLowerCase().replace(/\s+/g, "-")}`}
                  className="space-y-1"
                >
                  {items.map(({ key, to, label, icon: Icon, badge }) => (
                    <li key={key}>
                      <NavLink
                        to={to}
                        end={key === "dashboard" || key === "portal"}
                        data-testid={`nav-${key}`}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium border-l-2 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-2 ${
                            isActive
                              ? "bg-slate-50 text-slate-900 border-slate-900"
                              : "text-slate-500 border-transparent hover:text-slate-900 hover:bg-slate-50"
                          }`
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <Icon size={20} weight={isActive ? "duotone" : "regular"} />
                            <span className="flex-1">{label}</span>
                            {badge === "notifications" && unread > 0 && (
                              <span
                                data-testid="notifications-badge"
                                aria-label={`${unread} unread notifications`}
                                className="min-w-[20px] h-5 px-1.5 rounded-full bg-status-critical text-white text-[11px] font-semibold flex items-center justify-center"
                              >
                                {unread > 9 ? "9+" : unread}
                              </span>
                            )}
                          </>
                        )}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
        </nav>

        <div className="border-t border-slate-200 pt-3">
          <SupportContact compact />
          <div className="px-3 pb-3">
            <div className="flex items-center gap-3 px-3 py-2">
              <div className="w-9 h-9 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-semibold">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-900 truncate">{user?.name}</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {rolesForUser(user).map((role) => (
                    <span
                      key={role}
                      className={`inline-block text-[10px] px-1.5 py-0.5 rounded-full ${roleBadgeStyle(role)}`}
                    >
                      {roleLabel(role)}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="mt-2 w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-500 hover:text-status-critical hover:bg-status-critical/10 transition-colors duration-200"
            >
              <SignOut size={18} />
              Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      {/* `min-w-0` is load-bearing: a flex item defaults to min-width:auto and
          so refuses to shrink below its content's minimum. A wide table then
          pushed this column past the viewport and the whole page scrolled
          sideways, carrying the header buttons off the right edge with it. */}
      <main className="flex-1 min-w-0 ml-64 min-h-screen">
        {!organization && (
          <div
            data-testid="no-organization-banner"
            className="flex items-start gap-3 m-6 p-4 rounded-md border border-status-warn/30 bg-status-warn/10 text-sm text-status-warn"
          >
            <Warning size={20} weight="fill" className="shrink-0 mt-0.5" />
            <div>
              <div className="font-medium">This account has no organization membership.</div>
              <p className="mt-0.5 text-status-warn">
                {user?.memberships?.length > 1
                  ? "You belong to several organizations. Selecting between them is not available yet."
                  : "An administrator must grant you membership before laboratory data is accessible."}
              </p>
            </div>
          </div>
        )}
        <Outlet />
        <MessagingWidget />
      </main>
    </div>
  );
}
